// contexts/AdminContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import api, {
  createOrganization,
  getAllOrganizations,
  createUser,
  getAllUsers,
  grantFullAccess,
  getPremiumUsers,
  getPremiumWorkspaces,
  getAdminStats,
  getUsageOverview,
} from "../client/api";
import { Organization, User, Workspace, AdminStats } from "./types";

interface AdminContextType {
  organizations: Organization[];
  users: User[];
  premiumUsers: User[];
  premiumWorkspaces: Workspace[];
  stats: AdminStats | null;
  loading: boolean;
  error: string | null;
  fetchOrganizations: () => Promise<void>;
  fetchUsers: (filters?: {
    search?: string;
    premium?: boolean;
    role?: string;
  }) => Promise<void>;
  fetchPremiumData: () => Promise<void>;
  fetchStats: () => Promise<void>;
  addOrganization: (orgData: any) => Promise<Organization>;
  addUser: (userData: any) => Promise<User>;
  giveFullAccess: (
    userId: string,
    expiresAt?: Date,
    trialCredits?: number,
    trialCreditsVideoAgent?: number,
    trialCreditsPromptToVideo?: number,
  ) => Promise<User>;
  updateUser: (userId: string, userData: Partial<User>) => Promise<User>;
  updateWorkspaceLimits: (
    workspaceId: string,
    limits: {
      videoLimit?: number;
      heygenMonthlyLimit?: number;
      videoAgentMonthlyLimit?: number;
      promptToVideoMonthlyLimit?: number;
      avatarTotalLimit?: number;
    }
  ) => Promise<Workspace>;
  billingData: any[];
  billingTotals: {
    monthly: number;
    usage: number;
    selected: number;
  };
  billingType: string;
  fetchBillingData: (billingType?: string) => Promise<void>;
  monthlyBillingData: any;
  fetchMonthlyBilling: (
    month: number,
    year: number,
    billingType?: string
  ) => Promise<void>;
  usageOverview: any;
  fetchUsageOverview: (params?: {
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }) => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [premiumUsers, setPremiumUsers] = useState<User[]>([]);
  const [premiumWorkspaces, setPremiumWorkspaces] = useState<Workspace[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingData, setBillingData] = useState<any[]>([]);
  const [billingTotals, setBillingTotals] = useState({
    monthly: 0,
    usage: 0,
    selected: 0,
  });
  const [billingType, setBillingType] = useState("monthly");
  const [monthlyBillingData, setMonthlyBillingData] = useState<any>(null);
  const [usageOverview, setUsageOverview] = useState<any>(null);

  const fetchUsageOverviewData = async (params?: {
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    setLoading(true);
    try {
      const data = await getUsageOverview(params);
      setUsageOverview(data);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to fetch usage overview"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyBilling = async (
    month: number,
    year: number,
    type = "monthly"
  ) => {
    setLoading(true);
    try {
      const response = await api.get(
        `/api/admin/billing/monthly?month=${month}&year=${year}&billingType=${type}`
      );
      setMonthlyBillingData(response.data);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to fetch billing data"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async (type = "monthly") => {
    setLoading(true);
    try {
      const response = await api.get(`/api/admin/billing?billingType=${type}`);
      setBillingData(response.data.billingData);
      setBillingTotals(response.data.totals);
      setBillingType(response.data.billingType);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to fetch billing data"
      );
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId: string, userData: Partial<User>) => {
    setLoading(true);
    try {
      const response = await api.put(`/api/admin/users/${userId}`, userData);
      setUsers((prev) =>
        prev.map((u) => (u._id === userId ? response.data : u))
      );
      return response.data;
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to update user"
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await getAllOrganizations();
      setOrganizations(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch organizations"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (filters?: {
    search?: string;
    premium?: boolean;
    role?: string;
  }) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (filters?.search) params.append("search", filters.search);
      if (filters?.premium) params.append("premium", "true");
      if (filters?.role) params.append("role", filters.role);

      const response = await api.get(`/api/admin/users?${params.toString()}`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPremiumData = async () => {
    setLoading(true);
    try {
      const [users, workspaces] = await Promise.all([
        getPremiumUsers(),
        getPremiumWorkspaces(),
      ]);
      setPremiumUsers(users);
      setPremiumWorkspaces(workspaces);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch premium data"
      );
    } finally {
      setLoading(false);
    }
  };

  const updateWorkspaceLimits = async (
    workspaceId: string,
    limits: {
      videoLimit?: number;
      heygenMonthlyLimit?: number;
      videoAgentMonthlyLimit?: number;
      promptToVideoMonthlyLimit?: number;
      avatarTotalLimit?: number;
    }
  ) => {
    setLoading(true);
    try {
      const response = await api.put(
        `/api/admin/workspaces/${workspaceId}/limits`,
        limits
      );

      // Update the user's workspaces if they're in our state
      setUsers((prevUsers) =>
        prevUsers.map((user) => ({
          ...user,
          ownedWorkspaces:
            user.ownedWorkspaces?.map((ws) =>
              ws._id === workspaceId ? { ...ws, ...response.data } : ws
            ) || [],
        }))
      );

      // Also update premium workspaces if needed
      setPremiumWorkspaces((prevWorkspaces) =>
        prevWorkspaces.map((ws) =>
          ws._id === workspaceId ? { ...ws, ...response.data } : ws
        )
      );

      return response.data;
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Failed to update workspace limits"
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await getAdminStats();
      setStats({
        ...data,
        //monthlyRevenue: calculateMonthlyRevenue(data), // Implement this based on your pricing
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  };

  const addOrganization = async (orgData: any) => {
    setLoading(true);
    try {
      const newOrg = await createOrganization(orgData);
      setOrganizations((prev) => [...prev, newOrg]);
      return newOrg;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create organization"
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addUser = async (userData: any) => {
    setLoading(true);
    try {
      const newUser = await createUser(userData);
      setUsers((prev) => [...prev, newUser]);
      return newUser;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const giveFullAccess = async (
    userId: string,
    expiresAt?: Date,
    trialCredits?: number,
    trialCreditsVideoAgent?: number,
    trialCreditsPromptToVideo?: number,
  ) => {
    setLoading(true);
    try {
      const updatedUser = await grantFullAccess(
        userId,
        expiresAt,
        trialCredits,
        trialCreditsVideoAgent,
        trialCreditsPromptToVideo,
      );
      setUsers((prev) => prev.map((u) => (u._id === userId ? updatedUser : u)));
      return updatedUser;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to grant full access"
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial data fetch
    Promise.all([
      fetchOrganizations(),
      fetchUsers(),
      fetchPremiumData(),
      fetchStats(),
    ]);
  }, []);

  return (
    <AdminContext.Provider
      value={{
        organizations,
        users,
        premiumUsers,
        premiumWorkspaces,
        stats,
        loading,
        error,
        fetchOrganizations,
        fetchUsers,
        fetchPremiumData,
        fetchStats,
        addOrganization,
        addUser,
        giveFullAccess,
        updateUser,
        updateWorkspaceLimits,
        billingData,
        billingTotals,
        billingType,
        fetchBillingData,
        monthlyBillingData,
        fetchMonthlyBilling,
        usageOverview,
        fetchUsageOverview: fetchUsageOverviewData,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
};
